import { createDefaultEffectClip } from '@/vfx/registry';
import { deserializeEffectTracks } from '@/vfx/serialization';

/** Coerce persisted effect lanes to `[[clips]]`. */
export function coerceEffectTracks(tracks) {
  if (!Array.isArray(tracks)) return [[]];
  const lanes = tracks.map((lane) =>
    (Array.isArray(lane) ? lane.filter((c) => c && typeof c === 'object') : []),
  );
  return lanes.length ? lanes : [[]];
}

/** Collapse multi-lane / duplicate snapshots into a single timeline lane (dedupe by clip id). */
export function normalizeEffectTracks(tracks) {
  const coerced = coerceEffectTracks(tracks);
  const byId = new Map();
  coerced.flat().forEach((clip) => {
    if (!clip?.id) return;
    byId.set(String(clip.id), clip);
  });
  const merged = Array.from(byId.values()).sort((a, b) => {
    const startDelta = (Number(a.startTime) || 0) - (Number(b.startTime) || 0);
    if (startDelta !== 0) return startDelta;
    return String(a.id).localeCompare(String(b.id));
  });
  return [merged];
}

export function loadEffectTracksFromEditor(editor) {
  if (!editor || typeof editor !== 'object') return [[]];

  let tracks = null;
  if (Array.isArray(editor.effectTracks)) {
    const coerced = coerceEffectTracks(JSON.parse(JSON.stringify(editor.effectTracks)));
    if (coerced.some((lane) => lane.length > 0)) {
      tracks = coerced;
    }
  }
  if (!tracks && Array.isArray(editor.vfxEffects) && editor.vfxEffects.length) {
    tracks = deserializeEffectTracks(editor.vfxEffects);
  }
  return normalizeEffectTracks(tracks || [[]]);
}

export function patchEffectClip(tracks, clipId, patch) {
  return tracks.map((lane) =>
    lane.map((c) => {
      if (c.id !== clipId) return c;
      const next = { ...c, ...patch };
      if (patch.parameters) {
        next.parameters = { ...(c.parameters || {}), ...patch.parameters };
      }
      if (patch.keyframeTracks) {
        next.keyframeTracks = { ...(c.keyframeTracks || {}), ...patch.keyframeTracks };
      }
      return next;
    }),
  );
}

export function removeEffectClip(tracks, clipId) {
  return tracks.map((lane) => lane.filter((c) => c.id !== clipId));
}

export function clearAllEffectClips() {
  return [[]];
}

export function duplicateEffectClip(tracks, clip) {
  const copy = {
    ...JSON.parse(JSON.stringify(clip)),
    id: `vfx-${clip.type}-${Date.now()}`,
    startTime: clip.endTime + 0.1,
    endTime: clip.endTime + (clip.endTime - clip.startTime) + 0.1,
  };
  return tracks.map((lane, idx) => (idx === 0 ? [...lane, copy] : lane));
}

export function addEffectToTrack(tracks, type, playhead, duration = 5) {
  const clip = createDefaultEffectClip(type, playhead, playhead + duration);
  if (!clip) return tracks;
  return tracks.map((lane, idx) => (idx === 0 ? [...lane, clip] : lane));
}

export function beginEffectClipDrag({
  event,
  clip,
  trackIdx,
  pxPerSec,
  effectTrackRefs,
  setEffectTracks,
  setSelectedTimelineClip,
  setDraggingClipId,
  dragStateRef,
  clearWindowDragHandlers,
  installWindowDragHandlers,
}) {
  if (event.button !== 0) return;
  const sourceTrackNode = effectTrackRefs.current.get(trackIdx);
  if (!sourceTrackNode) return;

  event.preventDefault();
  setSelectedTimelineClip({ type: 'effect', id: clip.id });

  const sourceRect = sourceTrackNode.getBoundingClientRect();
  const clipLeftPx = clip.startTime * pxPerSec;
  const dragOffsetPx = Math.max(0, event.clientX - (sourceRect.left + clipLeftPx));

  dragStateRef.current = {
    clipId: clip.id,
    kind: 'effect',
    dragOffsetPx,
    sourceTrackNode,
  };
  setDraggingClipId(clip.id);

  const handleMove = (moveEvent) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.kind !== 'effect') return;
    const rect = dragState.sourceTrackNode.getBoundingClientRect();
    const newStart = Math.max(0, (moveEvent.clientX - rect.left - dragState.dragOffsetPx) / pxPerSec);
    const dur = Math.max(0.1, clip.endTime - clip.startTime);
    setEffectTracks((prev) =>
      prev.map((lane, idx) =>
        (idx === trackIdx
          ? lane.map((c) =>
            (c.id === clip.id
              ? { ...c, startTime: newStart, endTime: newStart + dur }
              : c),
          )
          : lane),
      ),
    );
  };

  const handleUp = () => {
    dragStateRef.current = null;
    setDraggingClipId(null);
    clearWindowDragHandlers(handleMove, handleUp);
  };

  installWindowDragHandlers(handleMove, handleUp);
}

export function beginEffectClipResize({
  event,
  clip,
  trackIdx,
  edge,
  pxPerSec,
  setEffectTracks,
  dragStateRef,
  clearWindowDragHandlers,
  installWindowDragHandlers,
}) {
  event.preventDefault();
  event.stopPropagation();
  const startX = event.clientX;
  const origStart = clip.startTime;
  const origEnd = clip.endTime;

  const handleMove = (moveEvent) => {
    const dx = (moveEvent.clientX - startX) / pxPerSec;
    let newStart = origStart;
    let newEnd = origEnd;
    if (edge === 'left') {
      newStart = Math.max(0, Math.min(origEnd - 0.1, origStart + dx));
    } else {
      newEnd = Math.max(origStart + 0.1, origEnd + dx);
    }
    setEffectTracks((prev) =>
      prev.map((lane, idx) =>
        (idx === trackIdx
          ? lane.map((c) =>
            (c.id === clip.id ? { ...c, startTime: newStart, endTime: newEnd } : c),
          )
          : lane),
      ),
    );
  };

  const handleUp = () => {
    dragStateRef.current = null;
    clearWindowDragHandlers(handleMove, handleUp);
  };

  installWindowDragHandlers(handleMove, handleUp);
}
